// ABOUTME: Confirmation asked before the cart is discarded: the question and a warning that it cannot be undone.
// ABOUTME: Discards through the cart context on confirm, closing on success; on failure it stays open with an error.

import { Alert, Stack, Typography } from '@ds/coolshop';
import { ConfirmDialog, ConfirmDialogResponse } from '@src/components/shared/ConfirmDialog';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useState } from 'react';
import { useIntl } from 'react-intl';

type DiscardCartDialogProps = {
    open: boolean;
    onClose: () => void;
    /** discards the cart; the dialog closes once it resolves and stays open when it rejects */
    onDiscard: () => Promise<void>;
};

export function DiscardCartDialog({ open, onClose, onDiscard }: DiscardCartDialogProps) {
    const { formatMessage } = useIntl();
    const [discarding, setDiscarding] = useState(false);

    const handleClose = async (response: ConfirmDialogResponse) => {
        if (response !== ConfirmDialogResponse.OK) {
            onClose();
            return;
        }
        setDiscarding(true);
        try {
            await onDiscard();
            onClose();
        } catch {
            // The cart is still there: say so, and leave the dialog open to retry or cancel.
            SnackBarUtils.error(
                formatMessage({
                    id: 'cpq.cart.discard.error',
                    defaultMessage: 'Failed to discard the cart',
                }),
            );
        } finally {
            setDiscarding(false);
        }
    };

    return (
        <ConfirmDialog
            isOpen={open}
            handleClose={handleClose}
            title={formatMessage({ id: 'cpq.cart.discard.title', defaultMessage: 'Discard cart' })}
            okLabel={formatMessage({ id: 'cpq.cart.discard.confirm', defaultMessage: 'Discard' })}
            loading={discarding}
            // While the cart is being discarded the dialog cannot be dismissed.
            disableCancel={discarding}
            maxWidth="sm"
            fullWidth
        >
            <Stack gap={2}>
                <Typography variant="body2">
                    {formatMessage({
                        id: 'cpq.cart.discard.message',
                        defaultMessage: 'Are you sure you want to discard the cart?',
                    })}
                </Typography>
                <Alert severity="warning">
                    {formatMessage({
                        id: 'cpq.cart.discard.irreversible',
                        defaultMessage: 'This operation cannot be undone.',
                    })}
                </Alert>
            </Stack>
        </ConfirmDialog>
    );
}

export default DiscardCartDialog;
